import { Platform, useWindowDimensions, View } from "react-native";
import { AHText, Box, TouchableBox, useTheme, useLayoutContext } from "arkh";

import React from "react";

import { PageSettings, PageSettingsLeft } from "./PageSettings";

import Animated, {
  interpolate,
  useAnimatedStyle,
  SharedValue,
  FadeIn,
  FadeOut,
} from "react-native-reanimated";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { graphql, useFragment } from "react-relay";
import { WidgetNoteTextModeAccessoryView_page$key } from "./__generated__/WidgetNoteTextModeAccessoryView_page.graphql";
import { useKeyboard } from "../../../../OS/useKeyboard";
import { KeyboardExtender } from "react-native-keyboard-controller";

type Props = {
  openCamera(): void;
  page: WidgetNoteTextModeAccessoryView_page$key;
  openImagePicker(): void;
  widgetId: string;
  onDone(): void;
  onRemovePage(): void;
  widgetLayout: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  animation: SharedValue<number>;
};

export function WidgetNoteTextModeAccessoryView({
  page: pageKey,
  openCamera,
  openImagePicker,
  widgetId,
  onDone,
  widgetLayout,
  animation,
  onRemovePage,
}: Props) {
  const page = useFragment(
    graphql`
      fragment WidgetNoteTextModeAccessoryView_page on WidgetNotePage {
        id
        ...PageSettingsLeft_page
        ...PageSettings_page
      }
    `,
    pageKey
  );
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();

  const animatedLeft = useAnimatedStyle(() => ({
    transform: [
      {
        scale: interpolate(animation.value, [0, 0.6, 1], [0, 0, 1]),
      },
    ],
  }));
  const animatedRight = useAnimatedStyle(() => ({
    transform: [
      {
        scale: interpolate(animation.value, [0, 0.6, 1], [0, 0, 1]),
      },
    ],
  }));

  const window = useWindowDimensions();
  const theme = useTheme();
  const hasWide = window.width >= window.height;

  if (Platform.OS === "web" || hasWide) {
    return (
      <>
        <Animated.View
          style={[
            {
              position: "absolute",
              right:
                widgetLayout.left + layout.space.large + widgetLayout.width,
              top: widgetLayout.top + widgetLayout.height - 70 - 5,
              height: 70,
              zIndex: 100,
              borderRadius: layout.radius.medium,
            },
            animatedLeft,
          ]}
        >
          <PageSettingsLeft
            page={page}
            openCamera={openCamera}
            openImagePicker={openImagePicker}
            widgetId={widgetId}
            onRemovePage={onRemovePage}
          />
        </Animated.View>
        <Animated.View
          style={[
            {
              position: "absolute",
              left: widgetLayout.left + widgetLayout.width + layout.space.large,
              top: widgetLayout.top + widgetLayout.height - 70 - 5,
              height: 70,
              width: 120,
              zIndex: 1,
              overflow: "hidden",
              borderRadius: layout.radius.medium,
              backgroundColor: theme["surface-primary"],
            },
            animatedRight,
          ]}
        >
          <TouchableBox
            onPress={onDone}
            spaceHorizontal="large"
            color="surface"
            style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
          >
            <AHText type="title-small" color="primary">
              {intl_tokens.Done}
            </AHText>
          </TouchableBox>
        </Animated.View>
      </>
    );
  }

  if (Platform.OS === "ios") {
    return (
      <KeyboardAccessoryView>
        <PageSettings
          page={page}
          openCamera={openCamera}
          openImagePicker={openImagePicker}
          widgetId={widgetId}
          onDone={onDone}
          onRemovePage={onRemovePage}
        />
      </KeyboardAccessoryView>
    );
  }
  if (Platform.OS === "android") {
    return (
      <KeyboardExtender>
        <View
          style={{
            height: 50,
            overflow: "hidden",
            backgroundColor: theme["surface-background"],
          }}
        >
          <PageSettings
            page={page}
            openCamera={openCamera}
            openImagePicker={openImagePicker}
            widgetId={widgetId}
            onDone={onDone}
            onRemovePage={onRemovePage}
          />
        </View>
      </KeyboardExtender>
    );
  }

  return (
    <Box
      style={{ alignSelf: "flex-end", height: 50, width: "100%", zIndex: 10 }}
      color="background"
    >
      <PageSettings
        onRemovePage={onRemovePage}
        page={page}
        openCamera={openCamera}
        openImagePicker={openImagePicker}
        widgetId={widgetId}
        onDone={onDone}
      />
    </Box>
  );
}

function KeyboardAccessoryView(props: { children: React.ReactNode }) {
  const keyboard = useKeyboard();

  const theme = useTheme();
  return keyboard.keyboardShown ? (
    <Animated.View
      entering={Platform.OS === "web" ? undefined : FadeIn}
      exiting={Platform.OS === "web" ? undefined : FadeOut}
      style={[
        {
          position: "absolute",
          alignSelf: "center",
          bottom: keyboard.keyboardHeight,
          height: 50,
          zIndex: 10,
          width: "100%",
          backgroundColor: theme["surface-background"],
        },
        // style,
      ]}
    >
      {props.children}
    </Animated.View>
  ) : null;
}
